import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  UserPlus,
  MoreVertical,
  Eye,
  Edit,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Loader,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { userAPI } from '../utils/api';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterKyc, setFilterKyc] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalDocs: 0
  });

  // Fetch users on component mount and when filters change
  useEffect(() => {
    fetchUsers();
  }, [pagination.page, filterStatus, filterKyc]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchUsers();
      } else {
        fetchUsers();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      // Add filters
      if (filterStatus !== 'all') {
        params.suspended = filterStatus === 'suspended';
      }
      if (filterKyc !== 'all') {
        params.kyc_tier = filterKyc;
      }

      const response = await userAPI.getUsers(params);

      if (response.data.success) {
        const data = response.data.data;
        setUsers(data.docs || []);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages || 1,
          totalDocs: data.totalDocs || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userAPI.searchUsers(searchTerm);

      if (response.data.success) {
        setUsers(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          page: 1,
          totalPages: 1,
          totalDocs: response.data.data?.length || 0
        }));
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to suspend ${userName}?`)) {
      return;
    }

    try {
      const reason = prompt('Enter suspension reason:');
      if (!reason) return;

      const response = await userAPI.suspendUser(userId, reason);

      if (response.data.success) {
        alert('User suspended successfully');
        fetchUsers(); // Refresh list
      }
    } catch (error) {
      console.error('Error suspending user:', error);
      alert('Failed to suspend user. Please try again.');
    }
  };

  const handleUnsuspendUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to unsuspend ${userName}?`)) {
      return;
    }

    try {
      const response = await userAPI.unsuspendUser(userId);

      if (response.data.success) {
        alert('User unsuspended successfully');
        fetchUsers(); // Refresh list
      }
    } catch (error) {
      console.error('Error unsuspending user:', error);
      alert('Failed to unsuspend user. Please try again.');
    }
  };

  const getStatusBadge = (user) => {
    if (user.suspended) {
      return <span className="badge-error">Suspended</span>;
    }
    if (user.is_active === false) {
      return <span className="badge-warning">Inactive</span>;
    }
    return <span className="badge-success">Active</span>;
  };

  const getKycBadge = (kycTier, kycStatus) => {
    if (!kycTier || kycTier === 'none') {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Not Verified</span>;
    }

    const tierColors = {
      basic: 'bg-blue-100 text-blue-800',
      enhanced: 'bg-purple-100 text-purple-800',
      advanced: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${tierColors[kycTier] || 'bg-gray-100 text-gray-800'}`}>
        {kycTier.charAt(0).toUpperCase() + kycTier.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getTimestampFromId = (id) => {
    if (!id) return null;
    try {
      const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
      return new Date(timestamp);
    } catch (error) {
      return null;
    }
  };

  const formatDate = (dateString, userId) => {
    // If dateString exists, use it
    if (dateString) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }

    // Fallback to extracting timestamp from MongoDB ObjectId
    if (userId) {
      const date = getTimestampFromId(userId);
      if (date) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }

    return 'N/A';
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleCloseModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Users</h1>
          <p className="mt-2 text-vetme-gray-dark">
            Manage user accounts, KYC verification, and access controls
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={fetchUsers}
            className="btn-vetme-outline flex items-center"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button className="btn-vetme-primary flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card-vetme p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-vetme-gray-dark w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-vetme pl-10 w-full"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-vetme w-full"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          {/* KYC Filter */}
          <div>
            <select
              value={filterKyc}
              onChange={(e) => setFilterKyc(e.target.value)}
              className="input-vetme w-full"
            >
              <option value="all">All KYC Levels</option>
              <option value="none">Not Verified</option>
              <option value="basic">Basic</option>
              <option value="enhanced">Enhanced</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card-vetme overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-vetme-gray-light">
            <thead className="bg-vetme-gray-lightest">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  KYC Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-vetme-gray-light">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Loader className="w-8 h-8 text-vetme-mint-dark animate-spin mx-auto" />
                    <p className="mt-2 text-vetme-gray-dark">Loading users...</p>
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-vetme-gray-lightest">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-vetme-mint-very-pale rounded-full flex items-center justify-center">
                          <span className="text-vetme-mint-dark font-medium">
                            {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-vetme-dark">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-vetme-gray-dark">
                            ID: {user.user_id || user._id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-vetme-dark">{user.email || 'N/A'}</div>
                      <div className="text-sm text-vetme-gray-dark">{user.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getKycBadge(user.kyc_tier, user.kyc_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-dark">
                      {formatCurrency(user.wallet_balance || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-gray-dark">
                      {formatDate(user.createdAt, user.id || user._id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewUserDetails(user)}
                          className="text-vetme-mint-dark hover:text-vetme-mint"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {user.suspended ? (
                          <button
                            onClick={() => handleUnsuspendUser(user._id, `${user.first_name} ${user.last_name}`)}
                            className="text-green-600 hover:text-green-800"
                            title="Unsuspend User"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSuspendUser(user._id, `${user.first_name} ${user.last_name}`)}
                            className="text-red-600 hover:text-red-800"
                            title="Suspend User"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-vetme-gray-dark">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-vetme-gray-light">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-vetme-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-vetme-outline ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-vetme-gray-dark">
                  Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span> ({pagination.totalDocs} total users)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="btn-vetme-outline rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="btn-vetme-outline rounded-r-md ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={handleCloseModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              {/* Header */}
              <div className="bg-vetme-dark px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">User Details</h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-white hover:text-vetme-mint"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
                {/* User Info */}
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div>
                    <h4 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Full Name</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.first_name} {selectedUser.last_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">User ID</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.user_id || selectedUser._id || selectedUser.id}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Email</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.email || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Phone</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.phone_ext && `+${selectedUser.phone_ext} `}{selectedUser.phone}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Joined</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {formatDate(selectedUser.createdAt, selectedUser.id || selectedUser._id)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Status</p>
                        <div className="mt-1">
                          {getStatusBadge(selectedUser)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* KYC Information */}
                  <div>
                    <h4 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">KYC Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-vetme-gray-dark">KYC Level</p>
                        <div className="mt-1">
                          {getKycBadge(selectedUser.kyc_tier, selectedUser.kyc_status)}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Verification Status</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.is_kyc_verified ? (
                            <span className="text-green-600">✓ Verified</span>
                          ) : (
                            <span className="text-orange-600">Not Verified</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Balance */}
                  <div>
                    <h4 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Wallet Balance</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Total Balance</p>
                        <p className="text-lg font-bold text-vetme-dark">
                          {formatCurrency(selectedUser.wallet_balance || 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Referral Info */}
                  {selectedUser.referral_stats && (
                    <div>
                      <h4 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Referral Information</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-vetme-gray-dark">Referral Code</p>
                          <p className="text-sm font-medium text-vetme-dark">
                            {selectedUser.referral_stats.personal_referral_code || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-vetme-gray-dark">Total Referrals</p>
                          <p className="text-sm font-medium text-vetme-dark">
                            {selectedUser.referral_stats.total_referrals || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-vetme-gray-dark">Successful Referrals</p>
                          <p className="text-sm font-medium text-vetme-dark">
                            {selectedUser.referral_stats.successful_referrals || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-vetme-gray-dark">Total Earnings</p>
                          <p className="text-sm font-medium text-vetme-dark">
                            {formatCurrency(selectedUser.referral_stats.total_earnings || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Account Settings */}
                  <div>
                    <h4 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Account Settings</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Transaction PIN</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.has_transaction_pin ? (
                            <span className="text-green-600">✓ Set</span>
                          ) : (
                            <span className="text-orange-600">Not Set</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Email Verified</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.email_verified_at ? (
                            <span className="text-green-600">✓ Verified</span>
                          ) : (
                            <span className="text-orange-600">Not Verified</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Phone Verified</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.phone_verified_at ? (
                            <span className="text-green-600">✓ Verified</span>
                          ) : (
                            <span className="text-orange-600">Not Verified</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">User Type</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedUser.user_type || 'Regular'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={handleCloseModal}
                  className="btn-vetme-outline"
                >
                  Close
                </button>
                {selectedUser.suspended ? (
                  <button
                    onClick={() => {
                      handleUnsuspendUser(selectedUser._id || selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`);
                      handleCloseModal();
                    }}
                    className="btn-vetme-primary bg-green-600 hover:bg-green-700"
                  >
                    Unsuspend User
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleSuspendUser(selectedUser._id || selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`);
                      handleCloseModal();
                    }}
                    className="btn-vetme-primary bg-red-600 hover:bg-red-700"
                  >
                    Suspend User
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
